<template>
    <b-modal :visible="visible" centered size="lg" scrollable no-close-on-backdrop no-close-on-esc body-class="pt-0" >
        <template #modal-header="{close}">
            <b-col cols="3">
                <!-- Back' -> "Retour" -->
                <b-icon-arrow-left role="button" font-scale="1" @click="visible = false; close();" v-b-tooltip.hover :title="$t('Back')"/> 
            </b-col>
            <b-col class="text-center">
                <!-- Modal Titre -->
                <h6>{{ Title }}</h6>
            </b-col>
            <b-col cols="3" class="text-right">
                 <!-- Close' -> "Fermer" -->
                <b-icon-x-lg role="button" font-scale="1" @click="visible = false; close();" v-b-tooltip.hover :title="$t('Close')" />
            </b-col>
        </template>
        <b-row class="py-2 px-3 sticky-top bg-white">
            <!-- 'PleaseEnterYourSearch' -> "Veuillez entrer votre recherche" -->
            <b-input-group>
                <b-form-input 
                v-model="searchValue" 
                autofocus 
                @keydown.enter="executeSearch();"
                :placeholder="$t('PleaseEnterYourSearch')"/>
                <template #append>
                     <!-- 'Search' -> "Rechercher" -->
                    <b-button @click="executeSearch();" :variant="Variant">{{$t('Search')}}</b-button>
                </template>
            </b-input-group>
        </b-row>
        <!-- 'NoResults' -> "Aucun résultat" -->
        <b-alert v-if="results.length == 0 && error" show variant="danger">{{ $t('NoResult') }}</b-alert>
        <template v-if="results.length > 0">
            <b-row class="py-2">
                <b-table :id="$id('searchTable')" select-mode="single" selectable responsive :items="results" :fields="searchFields" :current-page="currentPage" :per-page="itemPerPage" no-sort-reset @row-selected="setSelection" class="m-0" tbody-class="overflow-auto" tbody-tr-class="text-truncate">
                    <template #cell(ObjectType)="data">
                        <b-icon :icon="data.item.Icon"/>
                    </template>

                    <template #cell(Name)="data">
                        <p class="p-0 m-0" v-b-tooltip.noninteractive.hover :title="data.item.Location">
                            {{ data.item.Name }}
                        </p>
                        <!-- <p class="p-0 m-0 font-italic text-muted">{{ data.item.Location }}</p> -->
                    </template>

                    <template #cell(Properties)="data">
                        <!-- 'Properties.Labels.RAEPCENumber', { value: data.value.RAEPCENumber }) -> "RAE / PCE : 123456" -->
                        <p class="p-0 m-0">{{ $t('Headers.Properties.RAEPCENumber', { value: data.value.RAEPCENumber }) }}</p>
                        <!-- 'Properties.Labels.SIRET', { value: data.value.SIRET }) -> "SIRET : 123456789" -->
                        <p class="p-0 m-0">{{ $t('Headers.Properties.SIRET', { value: data.value.SIRET }) }}</p>
                        <!-- 'Properties.Labels.OperatEFA', { value: data.value.OperatEFA }) -> "OperatEFA : ABC123" -->
                        <p class="p-0 m-0">{{ $t('Headers.Properties.OperatEFA', { value: data.value.OperatEFA }) }}</p>
                    </template>
                </b-table> 
            </b-row>
        </template>
        <template #modal-footer>
            <b-pagination v-if="results.length > 0" v-model="currentPage" :total-rows="results.length" :per-page="itemPerPage" class="mr-auto" :style="generateVariantStyle(Variant)"/>
                <!-- 'Validate' -> Valider -->
            <b-button @click="validSelection()" :disabled="!(selectedNode && results.length != 0)" :variant="Variant" pill class="ml-auto">{{ $t('Validate') }}</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">

/** Composant Vue.js */
import {Vue, PropSync,  Prop } from 'vue-property-decorator';
import Component, { mixins } from 'vue-class-component';

/** Outils */
import { LinkUtils, ThemeUtils } from "@/components/commons/Utils";

/** Arbre générique non lié à l'arborescence patrimoine */
import { GenericTree, GenericTreeNode } from './GenericTreeClass';

/** Affiche une modal de recherche de patrimoine ainsi que les resultats de cette recherche */
@Component({
    components: {},
})
export default class GenericTreeSearch extends mixins(LinkUtils, ThemeUtils) {

    /** Variant de couleur à utiliser sur le tableau de bord */
    @Prop({ default: "primary" })
    public Variant!: string;

    /** Titre de la modal de recherche */
    @Prop()
    public Title!: string;

    @PropSync('Visible', { default: () => false }) /** type: boolean, */
    public visible!: boolean;

    @Prop()
    /** Arbre source su lequel effectuer la recherche */
    public RenderTree!: GenericTree;

    /** Valeur de recherche */
    private searchValue: string = "";

    /**Page courante utilisée par la pagination */
    private currentPage: number = 1;
    /** Nombre d'élément affiché par page */
    private itemPerPage: number = 6;
    /** Affiche une érreur ? */
    private error: boolean = false;

    /** Tableau des résultats */
    private results: GenericTreeNode[] = [];
    /** Noeud selectionné */
    private selectedNode: GenericTreeNode = {} as GenericTreeNode;

    /** Tableau des entêtes du tableau */
    private searchFields = [
        {
            key:"ObjectType",
            label:"",
            tdClass: "align-middle py-1 w-auto",
            sortable : false
        },
        {
            key:"Name",
            label:this.$t('Name').toString(),
            sortable : true
        },
        {
            key:"Location",
            label:this.$t('Location').toString(),
            sortable : true
        },
    ]

    /**
     * Execute la recherche et récupère les resultat
     * @param searchValue valeur à rechercher
     */
    private executeSearch() {
        const search = (node: GenericTreeNode): GenericTreeNode[] =>{
            /** Intialise le résultat */
            const result: GenericTreeNode[] = [];
            /** Test si il doit être présent dans le résultat */
            if(!node.Hidden && node.Name.toLowerCase().includes(this.searchValue.toLowerCase())) 
                result.push(node);
            /** Inclus le résultat des fils */
            for(let child of node.ChildNodes){
                for(let res of search(child)){
                    result.push(res);
                }
            }
            return result;
        } 

        /** Execute la recherche */
        for(let root of this.RenderTree.Nodes){
            for(let res of search(root)){
                this.results.push(res);
            }
        }
        if(this.results.length < 1)
            this.error = true;
    }

    /**
     * Attribut le noeud sélectionné à la variable searchNode
     * @param rows (Liste des éléments sélectionnés)
     */
    private setSelection(rows: GenericTreeNode[]){
        if(rows.length > 0){
            this.selectedNode = rows[0];
        } else {
            this.selectedNode = undefined;
        }
    }

    /** Validation de la sélection  */
    private validSelection(){
        this.visible = false;
        this.$emit('NodeSelectedEvent', this.selectedNode);
    }
}
</script>

<style scoped>
/** Pour que la page active prenne en compte la couleur de Variant */
:deep(.page-item.active .page-link) {
    background-color: var(--Variant) !important;
}

:deep(.page-link:hover) {
    background-color: var(--VariantLight) !important;
}
</style>